/**
 * People in the street, as drivers see them and as they see the cars.
 *
 * A driver reads a person against the road it is about to drive: the points
 * along its lane, and the next, every couple of metres ahead (`readPerson`).
 * Someone in the way, or about to step into it, is something to stop short
 * of, as for a car standing in the lane. Someone close beside the way is to
 * be passed slowly. A driver brakes for a person harder than for traffic
 * (`PERSON_BRAKE`), and nobody is ever driven into.
 *
 * A person waiting at the kerb looks both ways and crosses only into a gap
 * (`safeToCross`): every car coming either far enough off to be crossed in
 * front of with time to spare, or, at a mapped crossing, slow enough that it
 * is plainly stopping for them, as Swedish law says it must.
 *
 * Drivers have tempers (`pickTemperament`): most are patient, and a few are
 * not, follow closer, pass sooner and sound the horn at whoever stands in
 * their way.
 */

/** A person in the street as the traffic sees them, in scene metres. */
export interface StreetPerson {
  x: number;
  y: number;
  z: number;
  /** Velocity, m/s. */
  vx: number;
  vz: number;
  /**
   * Waiting at a mapped crossing: the point on the road they mean to walk
   * to, which a driver who can stop comfortably treats as someone already
   * there. NaN when not waiting.
   */
  aimX: number;
  aimZ: number;
}

/** What a person means for a car about to drive past them. */
export interface PersonReading {
  /** How far along its road the car must stop short of them, metres from its middle, or Infinity. */
  stopAt: number;
  /** How far along its road it passes close by them, and should be slow, or Infinity. */
  passAt: number;
}

/** Closer than this to the side of a car's body, a person is in its way. */
export const IN_THE_WAY = 0.6;
/**
 * Within this of the side of a car's body, a person is passed slowly. A
 * little over the 1.5 m that counts as close, for a body swinging wide of
 * its line through a turn.
 */
export const CLOSE_BY = 1.8;
/** How fast a car passes someone close by, m/s: walking pace. */
export const PASSING_SPEED = 1.5;
/** How hard a car brakes for a person, m/s²: an emergency stop, not traffic's 8. */
export const PERSON_BRAKE = 9.5;
/** How far ahead a person's walk is followed, seconds: someone about to step out is in the way already. */
const FORESIGHT = 1.2;
/** How hard a driver will brake to stop for someone waiting at a crossing, m/s²: harder than that, and it is too late to. */
const YIELD_BRAKE = 3.5;

/**
 * Read a person against the road ahead of a car: `path` is x, z pairs every
 * `step` metres along it, from the car's middle (s = 0) forward. `halfWidth`
 * is the car's. Where they are and where they will be a moment from now both
 * count; the nearer is what matters.
 */
export function readPerson(
  path: ArrayLike<number>, count: number, step: number, halfWidth: number,
  person: StreetPerson, out: PersonReading,
): PersonReading {
  out.stopAt = Infinity;
  out.passAt = Infinity;
  const fx = person.x + person.vx * FORESIGHT, fz = person.z + person.vz * FORESIGHT;
  for (let k = 0; k < count; k++) {
    // To the stretch of road from this point on to the next, not to the
    // point alone: a person between two points is nearer the road than either.
    const x = path[k * 2], z = path[k * 2 + 1];
    const ex = k + 1 < count ? path[k * 2 + 2] - x : 0, ez = k + 1 < count ? path[k * 2 + 3] - z : 0;
    const now = toSegment(person.x, person.z, x, z, ex, ez) - halfWidth;
    const soon = toSegment(fx, fz, x, z, ex, ez) - halfWidth;
    const clearance = Math.min(now, soon);
    if (clearance < IN_THE_WAY) {
      out.stopAt = k * step;
      if (out.passAt === Infinity) out.passAt = k * step;
      return out;
    }
    if (clearance < CLOSE_BY && out.passAt === Infinity) out.passAt = k * step;
  }
  return out;
}

/**
 * How far a person at (px, pz) is from a car's body: a box at (x, z) facing
 * unit (dx, dz). 0 inside it. Someone this close beside it, or just behind
 * it, is still passed slowly, though no longer ahead.
 */
export function bodyClearance(
  x: number, z: number, dx: number, dz: number, halfLength: number, halfWidth: number, px: number, pz: number,
): number {
  const rx = px - x, rz = pz - z;
  const along = Math.abs(rx * dx + rz * dz) - halfLength;
  const across = Math.abs(rx * dz - rz * dx) - halfWidth;
  return Math.hypot(Math.max(0, along), Math.max(0, across));
}

/** How far (px, pz) is from the segment from (x, z) along (ex, ez). */
function toSegment(px: number, pz: number, x: number, z: number, ex: number, ez: number): number {
  const len2 = ex * ex + ez * ez;
  const t = len2 > 1e-9 ? Math.max(0, Math.min(1, ((px - x) * ex + (pz - z) * ez) / len2)) : 0;
  return Math.hypot(px - x - ex * t, pz - z - ez * t);
}

/**
 * Whether a car `speed` m/s, `gap` metres short of where it would stop,
 * can stop for someone waiting at a crossing without braking harder than a
 * driver would for that.
 */
export function canYield(speed: number, gap: number): boolean {
  if (speed < 0.5) return true;
  return gap > 0 && (speed * speed) / (2 * gap) <= YIELD_BRAKE;
}

/**
 * The fastest a car may go `distance` metres before somewhere it must be
 * down to `speed`, braking at a comfortable `brake` m/s² to get there.
 */
export function approachSpeed(distance: number, speed: number, brake = 2.5): number {
  return Math.sqrt(speed * speed + 2 * brake * Math.max(0, distance));
}

/** A car as someone waiting at the kerb sees it: where it is against the line they will walk, and how fast it comes. */
export interface OncomingCar {
  /** Metres still to come before its front reaches the walking line; negative once past. */
  distance: number;
  /** Its speed toward the line, m/s. */
  speed: number;
  /** Whether its body is across the walking line now. */
  onLine: boolean;
}

/** A car as a box moving along its heading, scene metres. */
export interface CarBox {
  x: number;
  z: number;
  /** Unit heading. */
  dx: number;
  dz: number;
  speed: number;
  halfLength: number;
}

/**
 * Where `car` meets the line someone will walk across the road: from the
 * kerb at (kx, kz), `width` metres along unit (wx, wz). Undefined when its
 * way does not cross that line (it runs alongside, or passes beyond either
 * end) or is too far off to matter.
 */
export function oncoming(car: CarBox, kx: number, kz: number, wx: number, wz: number, width: number, out: OncomingCar): OncomingCar | undefined {
  const cross = car.dx * wz - car.dz * wx;
  if (Math.abs(cross) < 0.25) return undefined;
  const rx = kx - car.x, rz = kz - car.z;
  // car + s·heading = kerb + t·across.
  const s = (rx * wz - rz * wx) / cross;
  const t = (rx * car.dz - rz * car.dx) / cross;
  if (t < -1.5 || t > width + 1.5 || s > 90) return undefined;
  out.distance = s - car.halfLength;
  out.speed = car.speed;
  out.onLine = Math.abs(s) < car.halfLength + 0.7;
  return out;
}

/**
 * Whether someone may step out to cross `width` metres of road at `pace`
 * m/s, with the cars coming as they are. A car already past, or standing
 * clear of the line, is no matter. A moving one must be far enough off to
 * cross in front of with a margin to spare, or, at a mapped crossing, slow
 * enough that it is plainly stopping. A car across the line now is waited
 * out, standing or not.
 */
export function safeToCross(width: number, pace: number, zebra: boolean, cars: OncomingCar[]): boolean {
  const needed = width / Math.max(0.5, pace) + 1.5;
  for (const car of cars) {
    // Not through a car standing across the way, either.
    if (car.onLine) return false;
    if (car.distance < 0 || car.speed < 0.3) continue;
    if (car.distance / car.speed > needed) continue;
    // Stopping for them: slow, and with room to stop.
    if (zebra && car.speed < 4 && car.distance > 2) continue;
    return false;
  }
  return true;
}

/** How a driver drives among the others. */
export interface Temperament {
  /** Seconds held up by someone standing in its way before it sounds the horn. */
  patience: number;
  /** Seconds between one toot and the next, held up still. */
  nagging: number;
  /** Whether it passes sooner, and follows closer. */
  impatient: boolean;
}

/** A driver's temper from a roll in [0, 1) and another for the spread: one in eight is impatient. */
export function pickTemperament(roll: number, spread: number): Temperament {
  if (roll < 0.125) return { patience: 3 + spread * 2, nagging: 2.5 + spread, impatient: true };
  return { patience: 8 + spread * 7, nagging: 6 + spread * 4, impatient: false };
}
